import React, { useEffect } from "react";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { LogoStrip } from "@/components/site/LogoStrip";
import { Leistungen } from "@/components/site/Leistungen";
import { Referenzen } from "@/components/site/Referenzen";
import { Kpis } from "@/components/site/Kpis";
import { About } from "@/components/site/About";
import { Methode } from "@/components/site/Methode";
import { Faq } from "@/components/site/Faq";
import { Ablauf } from "@/components/site/Ablauf";
import { Ansprechpartner } from "@/components/site/Ansprechpartner";
import { Kontakt } from "@/components/site/Kontakt";
import { Footer } from "@/components/site/Footer";
import { FlowLines } from "@/components/site/FlowLines";
import { AccentH2, Container, Label, Reveal, Section } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";

const SubHero = ({ data }) => (
  <section data-testid="subpage-hero" className="has-lines relative overflow-hidden bg-[#1e2532] text-white on-dark">
    <FlowLines />
    <div className="hero-pad mx-auto grid max-w-[1240px] grid-cols-1 items-center gap-9 px-6 md:px-10 lg:grid-cols-[1.12fr_0.88fr] lg:gap-12">
      <div>
        <nav aria-label="Breadcrumb" data-testid="subpage-breadcrumb" className="mb-6 flex items-center gap-1.5 text-[13px] text-white/50">
          <a href="/" className="hover:text-white">Startseite</a>
          <ChevronRight size={13} />
          <span className="text-white/80">{data.breadcrumb}</span>
        </nav>
        <Label testId="subpage-kicker">{data.kicker}</Label>
        <h1 className="mt-7 font-bold" data-testid="subpage-h1">
          {data.h1Lines.map((line, i) => (
            <span
              className="block"
              key={line}
              style={{ color: i === data.h1Lines.length - 1 ? "var(--accent)" : undefined }}
            >
              {line}{i < data.h1Lines.length - 1 ? " " : ""}
            </span>
          ))}
        </h1>
        <p className="mt-6 max-w-[620px] text-[16.5px] leading-[1.72] text-white/72">{data.subline}</p>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <a
            href="#kontakt"
            data-testid="subpage-primary-cta"
            className="btn-solid inline-flex items-center px-7 py-[15px] text-[13.5px] font-bold uppercase tracking-[0.06em]"
          >
            <span>{data.primaryCta}</span>
          </a>
          <a
            href="#ablauf"
            data-testid="subpage-secondary-cta"
            className="inline-flex items-center gap-2.5 text-[15px] font-semibold text-white/90"
          >
            <span className="link-underline">{data.secondaryCta}</span>
            <ArrowRight size={17} />
          </a>
        </div>
      </div>
      <div className="img-frame media-hero w-full">
        <img src={data.heroImage} alt={data.heroImageAlt} width="1264" height="848" fetchPriority="high" decoding="async" className="h-full w-full" />
        <div className="pointer-events-none absolute inset-0 rounded-[6px] border border-white/10" />
      </div>
    </div>
  </section>
);

const Features = ({ data }) => (
  <Section testId="subpage-features" className="has-lines overflow-hidden">
    <FlowLines color="#0F9D6E" opacity={[0.035, 0.028]} />
    <Container>
      <Reveal>
        <Label>{data.label}</Label>
        <AccentH2 className="mt-6 max-w-[840px] font-bold text-[#1e2532]">{data.h2}</AccentH2>
        <p className="mt-6 max-w-[720px] text-[17px] leading-[1.8] text-[#565f70]">{data.intro}</p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {data.cards.map((c, i) => (
          <Reveal key={c.h3}>
            <div data-testid={`subpage-feature-${i}`} className="card-hover h-full rounded-[16px] border border-[#dfe2e0] bg-white p-7">
              <span className="font-display text-[13px] font-bold tracking-[0.14em] text-[#0f9d6e]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-4 text-[18px] font-semibold leading-[1.35] text-[#1e2532]">{c.h3}</h3>
              <span className="accent-rule mt-4" />
              <p className="mt-4 text-[15px] leading-[1.72] text-[#565f70]">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </Section>
);

const Flaechen = ({ data }) => (
  <Section testId="subpage-flaechen" className="has-lines overflow-hidden bg-[#f5f6f4]">
    <FlowLines color="#0F9D6E" opacity={[0.055, 0.045]} />
    <Container>
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
        <Reveal>
          <Label>{data.label}</Label>
          <AccentH2 className="mt-6 max-w-[560px] font-bold text-[#1e2532]">{data.h2}</AccentH2>
          <p className="mt-6 max-w-[600px] text-[17px] leading-[1.8] text-[#565f70]">{data.intro}</p>
          <ul className="mt-8 space-y-3.5">
            {data.items.map((it) => (
              <li key={it} className="flex gap-3 text-[16px] font-medium leading-[1.6] text-[#1e2532]">
                <span className="mt-[3px] flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-full bg-[#0f9d6e]">
                  <Check size={11} strokeWidth={3} className="text-white" />
                </span>
                {it}
              </li>
            ))}
          </ul>
          <p className="mt-8 border-t border-[#dfe2e0] pt-6 text-[15.5px] leading-[1.75] text-[#565f70]">{data.note}</p>
        </Reveal>
        <Reveal>
          <div className="img-frame aspect-[4/5] w-full max-w-[480px]">
            <img src={data.image} alt={data.imageAlt} loading="eager" decoding="async" width="1264" height="848" />
          </div>
        </Reveal>
      </div>
    </Container>
  </Section>
);

const Einbettung = ({ data }) => (
  <Section testId="subpage-einbettung" className="has-lines overflow-hidden bg-[#1e2532] text-white on-dark">
    <FlowLines />
    <Container>
      <Reveal>
        <Label>{data.label}</Label>
        <AccentH2 className="mt-6 max-w-[840px] font-bold text-white">{data.h2}</AccentH2>
        <p className="mt-6 max-w-[760px] text-[17px] leading-[1.8] text-white/70">{data.intro}</p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
        {data.links.map((l, i) => (
          <Reveal key={l.title}>
            <a
              href={l.href}
              data-testid={`subpage-service-link-${i}`}
              className="card-hover block h-full rounded-[16px] border border-white/12 bg-white/[0.04] p-7 md:p-8"
            >
              <h3 className="text-[18px] font-semibold text-white">{l.title}</h3>
              <p className="mt-3 text-[15px] leading-[1.72] text-white/65">{l.text}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.08em] text-[#3ecf9b]">
                Mehr erfahren <ArrowRight size={15} />
              </span>
            </a>
          </Reveal>
        ))}
      </div>
    </Container>
  </Section>
);

export default function SubPage({ data }) {
  useEffect(() => {
    document.title = data.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", data.metaDescription);
    window.scrollTo(0, 0);
  }, [data]);

  return (
    <>
      <Header />
      <main data-testid="subpage-main">
        <SubHero data={data} />
        <LogoStrip />
        {data.features && <Features data={data.features} />}
        {data.showLeistungen && <Leistungen />}
        {data.flaechen && <Flaechen data={data.flaechen} />}
        {data.cases && (
          <Section testId="subpage-cases" className="has-lines overflow-hidden bg-[#f5f6f4]">
            <FlowLines color="#0F9D6E" opacity={[0.055, 0.045]} />
            <Container>
              <Reveal>
                <Label>Referenzen</Label>
                <AccentH2 className="mt-6 max-w-[760px] font-bold text-[#1e2532]">Ergebnisse aus der Praxis</AccentH2>
              </Reveal>
              <div className={`mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 ${data.cases.length > 2 ? "lg:grid-cols-3" : ""} lg:gap-7`}>
                {data.cases.map((cs, i) => (
                  <Reveal key={cs.name}>
                    <article data-testid={`subpage-case-${i}`} className="card-hover flex h-full flex-col overflow-hidden rounded-[16px] border border-[#dfe2e0] border-t-[3px] border-t-[#0f9d6e] bg-white">
                      <div className="flex flex-1 flex-col p-7 md:p-8">
                        <div className="flex h-[46px] items-center">
                          <img src={cs.logo} alt={`Logo ${cs.name}`} loading="eager" decoding="async" className="h-[var(--ch)] w-auto max-w-[180px] object-contain" style={{ "--ch": `${cs.logoH || 40}px`, filter: "grayscale(1) contrast(1.05)" }} />
                        </div>
                        <h3 className="mt-6 text-[19px] font-semibold leading-[1.3] text-[#1e2532]">{cs.name}</h3>
                        <p className="mt-5 text-[15.5px] leading-[1.74] text-[#565f70]">{cs.text}</p>
                      </div>
                      <div className="mt-auto border-t border-[#e3ece7] bg-[#f2f8f5] px-7 py-6 md:px-8">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0b7d57]">Ergebnisse</p>
                        <ul className="mt-4 space-y-3">
                          {cs.metrics.map((m) => (
                            <li key={m} className="flex gap-3 text-[15px] font-medium leading-[1.55] text-[#1e2532]">
                              <span className="mt-[2px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#0f9d6e]">
                                <Check size={11} strokeWidth={3} className="text-white" />
                              </span>
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        )}
        {data.showReferenzen && (
          <>
            <Kpis />
            <Referenzen />
          </>
        )}
        {data.showAbout && (
          <>
            <About />
            <Methode />
          </>
        )}
        {data.einbettung && <Einbettung data={data.einbettung} />}
        {data.ratgeberTipp && (
          <section data-testid="ratgeber-tipp" className="border-y border-[#dfe2e0] bg-[#f2f8f5] py-10">
            <Container className="flex flex-wrap items-center justify-between gap-6">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0b7d57]">Aus dem Ratgeber</p>
                <p className="mt-2 text-[18px] font-semibold text-[#1e2532]">{data.ratgeberTipp.title}</p>
              </div>
              <a
                href={data.ratgeberTipp.href}
                className="group inline-flex items-center gap-2.5 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]"
              >
                <span className="link-underline">Artikel lesen</span>
                <ArrowRight size={16} />
              </a>
            </Container>
          </section>
        )}
        <Ablauf />
        {data.faq && <Faq items={data.faq.items} h2={data.faq.h2} label="FAQ" />}
        <Ansprechpartner />
        <Kontakt />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
